import { useApiClient } from '@saay/web-app';
import { formatCount, formatLocalDate, formatRatePercent, type LocalDate } from '@saay/formatting';
import { ListRow, Money, Notice } from '@saay/blocks';
import { TriangleAlertIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { CommandDialog } from '../components/command-dialog.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  NativeSelect,
  PageHeader,
  Section,
  Skeleton,
  TextField,
  useToast,
} from '../kit.js';
import { formatSignedRate, marketingLabel } from '../marketing-labels.js';
import type {
  MarketingAssignment,
  MarketingAssignmentCreate,
  MarketingAssignmentDeactivation,
  MarketingAssignmentSummary,
  MarketingAssignmentUpdate,
  MarketingImpactPreview,
} from '../marketing-types.js';
import { canMarket } from '../permissions.js';
import {
  listStatus,
  pageRows,
  setupKeys,
  useMarketingAssignment,
  useMarketingAssignments,
  useMarketingRecipients,
  useMarketingStructures,
  useMerchants,
  useOrganizations,
  useActiveOrganizationRoles,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import type { MarketingOutletContext } from './marketing.js';
import { CodeText, MarketingStatus, marketingGuidance, phoneSummary } from './marketing-ui.js';

function showDate(value: string): string {
  return formatLocalDate(value as LocalDate);
}

function scopeLabel(row: MarketingAssignmentSummary): string {
  if (row.scope.type === 'MERCHANT') return row.scope.merchant?.nameFa ?? 'فروشگاه';
  const role = row.scope.organizationRole?.nameFa;
  const name = row.scope.organization?.nameFa ?? 'سازمان';
  return role === undefined ? name : `${name} · ${role}`;
}

const columns: readonly DataTableColumn<MarketingAssignmentSummary>[] = [
  {
    header: 'محل',
    id: 'scope',
    render: (row) => <Link to={`/marketing/assignments/${row.id}`}>{scopeLabel(row)}</Link>,
  },
  {
    header: 'ساختار',
    id: 'structure',
    render: (row) => row.structure.nameFa,
  },
  {
    header: 'وضعیت',
    id: 'status',
    render: (row) => <MarketingStatus value={row.status} />,
  },
  {
    header: 'از',
    id: 'from',
    render: (row) => showDate(row.effectiveFrom),
  },
];

export function MarketingAssignmentsPage() {
  usePageTitle('اتصال کمیسیون');
  const { self } = useOutletContext<MarketingOutletContext>();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const scopeType = params.get('scopeType') ?? '';
  const list = useMarketingAssignments({
    ...(scopeType === '' ? {} : { scopeType }),
    ...(status === '' ? {} : { status }),
  });
  const rows = pageRows(list.data);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        actions={
          canMarket(self, 'createMarketingAssignment') ? (
            <Button onClick={() => setCreating(true)} type="button">
              اتصال تازه
            </Button>
          ) : undefined
        }
        title="اتصال کمیسیون"
      />
      <QueryState
        empty={<EmptyState title="اتصالی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اتصال‌ها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="marketing-assignments" title="فهرست اتصال‌ها">
          <FilterPillBar
            activeFilterCount={[status, scopeType].filter(Boolean).length}
            label="فیلتر اتصال‌ها"
            onClearAll={() => setParams({})}
          >
            <FilterPill
              emptyLabel="همه وضعیت‌ها"
              label="وضعیت"
              onChange={(value) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (value === '') next.delete('status');
                  else next.set('status', value);
                  return next;
                })
              }
              options={(['DRAFT', 'SCHEDULED', 'ACTIVE', 'ENDED', 'CANCELLED'] as const).map(
                (value) => ({ label: marketingLabel(value), value }),
              )}
              value={status}
            />
            <FilterPill
              emptyLabel="همه محل‌ها"
              label="محل اتصال"
              onChange={(value) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (value === '') next.delete('scopeType');
                  else next.set('scopeType', value);
                  return next;
                })
              }
              options={(['MERCHANT', 'BUYER_ORGANIZATION'] as const).map((value) => ({
                label: marketingLabel(value),
                value,
              }))}
              value={scopeType}
            />
          </FilterPillBar>
          <AdminListTable
            caption="فهرست اتصال‌ها"
            columns={columns}
            mobileSummary={(row) =>
              phoneSummary({
                meta: `${row.structure.nameFa} · ${showDate(row.effectiveFrom)}`,
                status: row.status,
                title: <Link to={`/marketing/assignments/${row.id}`}>{scopeLabel(row)}</Link>,
              })
            }
            rowKey={(row) => row.id}
            rows={rows}
          />
        </Section>
      </QueryState>
      {creating ? <CreateAssignmentDialog onOpenChange={setCreating} /> : null}
    </>
  );
}

const createSchema = z.object({
  effectiveFrom: z.string().min(1, 'تاریخ شروع را انتخاب کنید.'),
  effectiveTo: z.string(),
  merchantId: z.string(),
  organizationId: z.string(),
  organizationRoleId: z.string(),
  scopeType: z.enum(['MERCHANT', 'BUYER_ORGANIZATION']),
  structureId: z.string().min(1, 'ساختار را انتخاب کنید.'),
});
type CreateValues = z.input<typeof createSchema>;

function CreateAssignmentDialog({
  onOpenChange,
}: {
  readonly onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const structures = useMarketingStructures();
  const merchants = useMerchants({}, true);
  const organizations = useOrganizations({}, true);
  const roles = useActiveOrganizationRoles();
  const structureRows = pageRows(structures.data).filter((item) => item.publishedVersion !== null);
  return (
    <CommandDialog<CreateValues, MarketingAssignmentCreate, MarketingAssignment>
      defaultValues={{
        effectiveFrom: '',
        effectiveTo: '',
        merchantId: '',
        organizationId: '',
        organizationRoleId: '',
        scopeType: 'MERCHANT',
        structureId: '',
      }}
      fields={['effectiveFrom', 'merchantId', 'organizationId', 'structureId']}
      guidance={marketingGuidance}
      name="marketing.assignment.create"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.marketingAssignments });
        toast.success({ confirmedBy: result, title: 'اتصال پیش‌نویس شد' });
        void navigate(`/marketing/assignments/${result.data.id}`);
      }}
      schema={createSchema as never}
      send={(client, body, intent) => client.post('/admin/marketing-assignments', { body, intent })}
      size="wide"
      submitLabel="ثبت پیش‌نویس"
      title="اتصال تازه"
      toBody={(values) => {
        const parsed = createSchema.parse(values);
        const structure = structureRows.find((item) => item.id === parsed.structureId);
        const versionId = structure?.publishedVersion?.id;
        if (versionId === undefined) return null;
        const scope =
          parsed.scopeType === 'MERCHANT'
            ? { merchantId: parsed.merchantId, type: 'MERCHANT' as const }
            : {
                organizationId: parsed.organizationId,
                type: 'BUYER_ORGANIZATION' as const,
                ...(parsed.organizationRoleId === ''
                  ? {}
                  : { organizationRoleId: parsed.organizationRoleId }),
              };
        return {
          effectiveFrom: parsed.effectiveFrom,
          layerAssignees: [],
          scope,
          structureVersionId: versionId,
          ...(parsed.effectiveTo === '' ? {} : { effectiveTo: parsed.effectiveTo }),
        };
      }}
    >
      {(form) => {
        const scopeType = form.watch('scopeType');
        return (
          <>
            <div className="flex gap-2" role="group" aria-label="نوع اتصال">
              <Button
                onClick={() => form.setValue('scopeType', 'MERCHANT')}
                type="button"
                variant={scopeType === 'MERCHANT' ? 'primary' : 'secondary'}
              >
                فروشگاه
              </Button>
              <Button
                onClick={() => form.setValue('scopeType', 'BUYER_ORGANIZATION')}
                type="button"
                variant={scopeType === 'BUYER_ORGANIZATION' ? 'primary' : 'secondary'}
              >
                سازمان
              </Button>
            </div>
            {scopeType === 'MERCHANT' ? (
              <NativeSelect
                label="فروشگاه"
                onChange={(event) => form.setValue('merchantId', event.target.value)}
                options={pageRows(merchants.data).map((merchant) => ({
                  label: merchant.displayNameFa,
                  value: merchant.id,
                }))}
                placeholder="انتخاب کنید"
                value={form.watch('merchantId')}
              />
            ) : (
              <>
                <NativeSelect
                  label="سازمان"
                  onChange={(event) => form.setValue('organizationId', event.target.value)}
                  options={pageRows(organizations.data).map((organization) => ({
                    label: organization.displayNameFa,
                    value: organization.id,
                  }))}
                  placeholder="انتخاب کنید"
                  value={form.watch('organizationId')}
                />
                <NativeSelect
                  label="نقش سازمان (اختیاری)"
                  onChange={(event) => form.setValue('organizationRoleId', event.target.value)}
                  options={(roles.data ?? []).map((role) => ({
                    label: role.nameFa,
                    value: role.id,
                  }))}
                  placeholder="همهٔ اعضا"
                  value={form.watch('organizationRoleId')}
                />
              </>
            )}
            <NativeSelect
              label="ساختار منتشرشده"
              onChange={(event) => form.setValue('structureId', event.target.value)}
              options={structureRows.map((structure) => ({
                label: structure.nameFa,
                value: structure.id,
              }))}
              placeholder="انتخاب کنید"
              value={form.watch('structureId')}
            />
            <AdminSolarDateField
              label="از تاریخ"
              onChange={(value) => form.setValue('effectiveFrom', value, { shouldDirty: true })}
              value={form.watch('effectiveFrom')}
            />
            <p className="text-sm text-muted-foreground">
              لایه‌ها را در صفحهٔ اتصال به بازاریاب وصل کنید. تا فعال‌سازی، پیش‌نویس روی فروش اثر
              ندارد.
            </p>
          </>
        );
      }}
    </CommandDialog>
  );
}

const editSchema = z.object({
  assignees: z.array(z.object({ recipientId: z.string(), structureLayerId: z.string() })),
  effectiveFrom: z.string().min(1, 'تاریخ شروع را انتخاب کنید.'),
  effectiveTo: z.string(),
});
type EditValues = z.input<typeof editSchema>;

const activateSchema = z.object({
  acknowledge: z.boolean(),
  reason: reasonSchema(),
});
type ActivateValues = z.input<typeof activateSchema>;

const deactivateSchema = z.object({
  effectiveTo: z.string(),
  reason: reasonSchema(),
});
type DeactivateValues = z.input<typeof deactivateSchema>;

export function MarketingAssignmentPage() {
  const { assignmentId = '' } = useParams();
  const { self } = useOutletContext<MarketingOutletContext>();
  const assignment = useMarketingAssignment(assignmentId);
  const data = assignment.data;
  usePageTitle(data === undefined ? 'اتصال' : scopeLabel(data));
  const manage = canMarket(self, 'updateMarketingAssignment');
  const canPreview = canMarket(self, 'previewMarketingAssignmentImpact');
  const showMargin = data?.negativeMarginAcknowledged !== undefined || canPreview;
  return (
    <>
      <PageHeader
        eyebrow={<Link to="/marketing/assignments">اتصال‌ها</Link>}
        title={data === undefined ? 'اتصال' : scopeLabel(data)}
      />
      <QueryState
        error={assignment.error}
        onRetry={() => void assignment.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اتصال…" lines={6} />}
        status={assignment.status}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <Section id="assignment-summary" title="خلاصه">
              <p>
                ساختار {data.structure.nameFa} <CodeText>{data.structure.code}</CodeText>
              </p>
              <p>
                وضعیت <MarketingStatus value={data.status} />
              </p>
              <p>{`از ${showDate(data.effectiveFrom)}${data.effectiveTo === null ? '' : ` تا ${showDate(data.effectiveTo)}`}`}</p>
              <p>{`${formatCount(data.assignedLayerCount)} از ${formatCount(data.layerCount)} لایه وصل شده`}</p>
              {data.scope.organizationRole === null ? null : (
                <p>{`فقط نقش ${data.scope.organizationRole.nameFa}`}</p>
              )}
              {showMargin && data.negativeMarginAcknowledged === true ? (
                <p>حاشیهٔ منفی در زمان فعال‌سازی تأیید شده است.</p>
              ) : null}
            </Section>
            <Section id="assignment-layers" title="لایه‌ها">
              <ul className="flex flex-col gap-2">
                {data.layers.map((layer) => (
                  <li key={layer.structureLayerId}>
                    {`${formatCount(layer.layerNumber)}. ${layer.roleLabelFa} · ${marketingLabel(layer.baseType)} · ${formatRatePercent(layer.percentage, { invalid: 'raw' })} · ${
                      layer.recipient === null
                        ? 'بدون بازاریاب'
                        : `${layer.recipient.displayNameFa ?? layer.recipient.recipientCode}`
                    }`}
                  </li>
                ))}
              </ul>
            </Section>
            {manage && data.status === 'DRAFT' ? (
              <DraftEditor
                assignment={data}
                onSaved={async () => {
                  await assignment.refetch();
                }}
              />
            ) : null}
            {canPreview && data.status === 'DRAFT' ? (
              <ImpactPanel
                assignment={data}
                onActivated={async () => {
                  await assignment.refetch();
                }}
              />
            ) : null}
            {manage &&
            (data.status === 'DRAFT' || data.status === 'SCHEDULED' || data.status === 'ACTIVE') ? (
              <DeactivatePanel
                assignment={data}
                onDone={async () => {
                  await assignment.refetch();
                }}
              />
            ) : null}
          </div>
        )}
      </QueryState>
    </>
  );
}

function DraftEditor({
  assignment,
  onSaved,
}: {
  readonly assignment: MarketingAssignment;
  readonly onSaved: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const recipients = useMarketingRecipients({ status: 'ACTIVE' });
  const toast = useToast();
  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} type="button" variant="secondary">
        ویرایش پیش‌نویس
      </Button>
    );
  }
  return (
    <CommandDialog<EditValues, MarketingAssignmentUpdate, MarketingAssignment>
      defaultValues={{
        assignees: assignment.layers.map((layer) => ({
          recipientId: layer.recipient?.id ?? '',
          structureLayerId: layer.structureLayerId,
        })),
        effectiveFrom: assignment.effectiveFrom,
        effectiveTo: assignment.effectiveTo ?? '',
      }}
      fields={['effectiveFrom', 'effectiveTo']}
      guidance={marketingGuidance}
      name="marketing.assignment.update"
      onOpenChange={setOpen}
      onSuccess={async (result) => {
        await onSaved();
        toast.success({ confirmedBy: result, title: 'پیش‌نویس ذخیره شد' });
      }}
      schema={editSchema as never}
      send={(client, body, intent) =>
        client.patch(`/admin/marketing-assignments/${encodeURIComponent(assignment.id)}`, {
          body,
          intent,
        })
      }
      submitLabel="ذخیره پیش‌نویس"
      title="ویرایش اتصال"
      toBody={(values) => ({
        effectiveFrom: values.effectiveFrom,
        expectedVersion: assignment.version,
        layerAssignees: values.assignees
          .filter((item) => item.recipientId !== '')
          .map((item) => ({
            marketingRecipientId: item.recipientId,
            structureLayerId: item.structureLayerId,
          })),
        ...(values.effectiveTo === ''
          ? { effectiveTo: null }
          : { effectiveTo: values.effectiveTo }),
      })}
    >
      {(form) => (
        <>
          <AdminSolarDateField
            label="از تاریخ"
            onChange={(value) => form.setValue('effectiveFrom', value, { shouldDirty: true })}
            value={form.watch('effectiveFrom')}
          />
          <AdminSolarDateField
            label="تا تاریخ (اختیاری)"
            onChange={(value) => form.setValue('effectiveTo', value, { shouldDirty: true })}
            value={form.watch('effectiveTo')}
          />
          {form.watch('assignees').map((layer, index) => {
            const source = assignment.layers[index];
            return (
              <NativeSelect
                key={layer.structureLayerId}
                label={source === undefined ? 'لایه' : source.roleLabelFa}
                onChange={(event) =>
                  form.setValue(`assignees.${index}.recipientId`, event.target.value, {
                    shouldDirty: true,
                  })
                }
                options={pageRows(recipients.data).map((recipient) => ({
                  label: recipient.user.displayNameFa ?? recipient.recipientCode,
                  value: recipient.id,
                }))}
                placeholder="بدون بازاریاب"
                value={layer.recipientId}
              />
            );
          })}
        </>
      )}
    </CommandDialog>
  );
}

function ImpactPanel({
  assignment,
  onActivated,
}: {
  readonly assignment: MarketingAssignment;
  readonly onActivated: () => Promise<void>;
}) {
  const client = useApiClient();
  const toast = useToast();
  const [preview, setPreview] = useState<MarketingImpactPreview | null>(null);
  const [error, setError] = useState<unknown>(undefined);
  const [pending, setPending] = useState(false);
  const [activating, setActivating] = useState(false);

  async function loadPreview() {
    setPending(true);
    setError(undefined);
    try {
      const result = await client.postWithoutKey<MarketingImpactPreview>(
        `/admin/marketing-assignments/${encodeURIComponent(assignment.id)}/impact-previews`,
        { body: { exampleAmountIrr: 10_000_000 } },
      );
      setPreview(result.data);
    } catch (caught) {
      setPreview(null);
      setError(caught);
    } finally {
      setPending(false);
    }
  }

  const negative = preview?.snapshot.negativeMarginPossible === true;
  return (
    <Section id="assignment-impact" title="گزارش اثر">
      <Button disabled={pending} onClick={() => void loadPreview()} type="button">
        گزارش اثر
      </Button>
      {error === undefined ? null : <ErrorState error={error} title="گزارش اثر آماده نشد" />}
      {preview === null ? null : (
        <div className="flex flex-col gap-3">
          {negative ? (
            <Notice
              description="بدترین حالت، فروشگاه را با همهٔ اتصال‌های سازمان هم‌پوشان یک‌جا حساب می‌کند. فعال‌سازی بدون تأیید ممکن نیست."
              icon={TriangleAlertIcon}
              title="حاشیه سود ممکن است منفی شود"
              tone="copper"
            />
          ) : (
            <p>در این گزارش حاشیهٔ منفی دیده نمی‌شود.</p>
          )}
          <p>
            بدترین حاشیه:{' '}
            <CodeText>{formatSignedRate(preview.snapshot.worstCaseMarginRate)}</CodeText>
          </p>
          <p>
            مثال خرید <Money rials={preview.snapshot.example.amountIrr} /> · کارمزد{' '}
            <Money rials={preview.snapshot.example.saayFeeIrr} /> · کمیسیون{' '}
            <Money rials={preview.snapshot.example.worstCaseCommissionIrr} /> · حاشیه{' '}
            <Money rials={preview.snapshot.example.worstCaseMarginIrr} />
          </p>
          <p>{`${formatCount(preview.snapshot.combinationCount)} ترکیب`}</p>
          <ul>
            {preview.labels.merchants.map((merchant) => (
              <li key={merchant.id}>{merchant.nameFa}</li>
            ))}
            {preview.labels.organizations.map((organization) => (
              <li key={organization.id}>{organization.nameFa}</li>
            ))}
          </ul>
          {activating ? (
            <CommandDialog<
              ActivateValues,
              {
                acknowledgeNegativeMargin: boolean;
                expectedVersion: number;
                previewToken: string;
                reason: string;
                snapshotHash: string;
              },
              MarketingAssignment
            >
              defaultValues={{ acknowledge: false, reason: '' }}
              fields={['reason', 'acknowledge']}
              guidance={marketingGuidance}
              name="marketing.assignment.activate"
              onOpenChange={setActivating}
              onSuccess={async (result) => {
                await onActivated();
                toast.success({ confirmedBy: result, title: 'اتصال فعال شد' });
              }}
              schema={
                activateSchema.superRefine((value, context) => {
                  if (negative && !value.acknowledge) {
                    context.addIssue({
                      code: 'custom',
                      message: 'تأیید حاشیهٔ منفی لازم است.',
                      path: ['acknowledge'],
                    });
                  }
                }) as never
              }
              send={(api, body, intent) =>
                api.post(
                  `/admin/marketing-assignments/${encodeURIComponent(assignment.id)}/activation`,
                  { body, intent },
                )
              }
              submitLabel="فعال‌سازی"
              title="فعال‌سازی اتصال"
              toBody={(values) => ({
                acknowledgeNegativeMargin: values.acknowledge,
                expectedVersion: assignment.version,
                previewToken: preview.previewToken,
                reason: values.reason.trim(),
                snapshotHash: preview.snapshotHash,
              })}
            >
              {(form) => (
                <>
                  {negative ? (
                    <Checkbox
                      checked={form.watch('acknowledge')}
                      label="تأیید می‌کنم که حاشیه سود SAAY ممکن است منفی شود"
                      onChange={(event) =>
                        form.setValue('acknowledge', event.target.checked, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    />
                  ) : null}
                  <TextField
                    error={form.formState.errors.reason?.message}
                    label="دلیل"
                    {...form.register('reason')}
                  />
                </>
              )}
            </CommandDialog>
          ) : (
            <Button onClick={() => setActivating(true)} type="button">
              فعال‌سازی
            </Button>
          )}
        </div>
      )}
    </Section>
  );
}

function DeactivatePanel({
  assignment,
  onDone,
}: {
  readonly assignment: MarketingAssignment;
  readonly onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const draft = assignment.status === 'DRAFT';
  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} type="button" variant="secondary">
        {draft ? 'لغو پیش‌نویس' : 'پایان اتصال'}
      </Button>
    );
  }
  return (
    <CommandDialog<DeactivateValues, MarketingAssignmentDeactivation, MarketingAssignment>
      defaultValues={{ effectiveTo: '', reason: '' }}
      fields={['reason', 'effectiveTo']}
      guidance={marketingGuidance}
      name="marketing.assignment.deactivate"
      onOpenChange={setOpen}
      onSuccess={async (result) => {
        await onDone();
        toast.success({
          confirmedBy: result,
          title: draft ? 'پیش‌نویس لغو شد' : 'اتصال پایان یافت',
        });
      }}
      schema={deactivateSchema as never}
      send={(client, body, intent) =>
        client.post(
          `/admin/marketing-assignments/${encodeURIComponent(assignment.id)}/deactivation`,
          {
            body,
            intent,
          },
        )
      }
      submitLabel={draft ? 'لغو پیش‌نویس' : 'پایان اتصال'}
      title={draft ? 'لغو پیش‌نویس' : 'پایان اتصال'}
      toBody={(values) => ({
        expectedVersion: assignment.version,
        reason: values.reason.trim(),
        ...(draft || values.effectiveTo === '' ? {} : { effectiveTo: values.effectiveTo }),
      })}
    >
      {(form) => (
        <>
          {draft ? (
            <p>پیش‌نویس لغو می‌شود و روی فروش اثری نمی‌گذارد.</p>
          ) : (
            <AdminSolarDateField
              label="تا تاریخ (خالی یعنی همین حالا)"
              onChange={(value) => form.setValue('effectiveTo', value, { shouldDirty: true })}
              value={form.watch('effectiveTo')}
            />
          )}
          <TextField
            error={form.formState.errors.reason?.message}
            label="دلیل"
            {...form.register('reason')}
          />
        </>
      )}
    </CommandDialog>
  );
}

/** Assignments of one merchant or organization, read from the list endpoint. */
export function ScopeMarketingAssignments({
  merchantId,
  organizationId,
}: {
  readonly merchantId?: string;
  readonly organizationId?: string;
}) {
  const filters =
    merchantId !== undefined ? { merchantId } : { organizationId: organizationId ?? '' };
  const enabled = (merchantId ?? organizationId ?? '') !== '';
  const list = useMarketingAssignments(filters, enabled);
  const rows = pageRows(list.data);
  return (
    <SurfaceSheetSection id="scope-marketing" title="اتصال بازاریابی">
      <QueryState
        empty={<EmptyState title="اتصالی برای این مورد نیست" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اتصال‌ها…" lines={3} />}
        status={listStatus(list)}
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <ListRow
                description={showDate(row.effectiveFrom)}
                end={<MarketingStatus value={row.status} />}
                look="tile"
                title={<Link to={`/marketing/assignments/${row.id}`}>{row.structure.nameFa}</Link>}
              />
            </li>
          ))}
        </ul>
      </QueryState>
    </SurfaceSheetSection>
  );
}
